import { useState } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { exportProject, ExportError, type ExportHandle, type ExportProgress } from '@/lib/render/exportProject';
import { buildFileName } from '@/lib/filename';
import { SectionCard } from '@/components/SectionCard';
import { Select } from '@/components/Select';
import { Toggle } from '@/components/Toggle';
import { Button } from '@/components/Button';
import { ProgressBar } from '@/components/ProgressBar';
import type { ExportSettings, FrameRate, QualityPreset, ReelProject } from '@/types';

export function ExportPanel({ project }: { project: ReelProject }) {
  const updateExport = useWorkspaceStore((s) => s.updateExport);
  const shared = useWorkspaceStore((s) => s.workspace.shared);
  const debugMode = useWorkspaceStore((s) => s.debugMode);
  const e = project.export.overridesShared ? project.export : shared.export;

  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultFileName, setResultFileName] = useState<string | null>(null);
  const [handle, setHandle] = useState<ExportHandle | null>(null);

  function patch(fields: Partial<ExportSettings>) {
    updateExport(project.id, { ...fields, overridesShared: true });
  }

  const previewFileName = buildFileName({ title: project.title, exportSettings: e });

  async function handleExport() {
    setError(null);
    setResultUrl(null);
    setProgress({ stage: 'preparing', progress: 0 });
    try {
      const currentShared = useWorkspaceStore.getState().workspace.shared;
      const h = await exportProject(project, currentShared, { onProgress: setProgress });
      setHandle(h);
      const blob = await h.promise;
      const fileName = buildFileName({ title: project.title, exportSettings: e });
      setResultUrl(URL.createObjectURL(blob));
      setResultFileName(fileName);
    } catch (err) {
      if (err instanceof Error && err.name === 'RenderCancelledError') {
        setError('Export cancelled.');
      } else {
        setError(err instanceof ExportError ? err.message : 'Export failed. Enable debug mode below for technical details.');
        if (debugMode && err instanceof Error) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }
    } finally {
      setProgress(null);
      setHandle(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2 text-xs dark:bg-slate-800">
        <span className="text-slate-500 dark:text-slate-400">Export settings come from shared defaults unless overridden here.</span>
        <label className="flex shrink-0 items-center gap-2">
          <input type="checkbox" checked={project.export.overridesShared} onChange={(ev) => updateExport(project.id, { overridesShared: ev.target.checked })} />
          Override for this Reel
        </label>
      </div>

      <SectionCard title="Video settings">
        <div className="grid grid-cols-2 gap-2">
          <Select
            label="Frame rate"
            value={String(e.frameRate)}
            options={[24, 25, 30, 60].map((f) => ({ value: String(f), label: `${f} fps` }))}
            onChange={(v) => patch({ frameRate: Number(v) as FrameRate })}
          />
          <Select
            label="Quality"
            value={e.quality}
            options={[
              { value: 'draft', label: 'Draft (fast)' },
              { value: 'standard', label: 'Standard' },
              { value: 'high', label: 'High' },
            ]}
            onChange={(v) => patch({ quality: v as QualityPreset })}
          />
        </div>
        <p className="text-xs text-slate-400">Resolution: {e.widthPx}×{e.heightPx} (9:16) · H.264 / AAC MP4</p>
        <Toggle
          label="Match duration to voiceover"
          checked={e.matchDurationToVoiceover}
          onChange={(v) => patch({ matchDurationToVoiceover: v })}
          tooltip="Otherwise the video's length is driven by the image sequence."
        />
      </SectionCard>

      <SectionCard title="File name">
        <div className="flex gap-2">
          <button
            onClick={() => patch({ fileNameMode: 'from-title' })}
            className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${e.fileNameMode === 'from-title' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30' : 'border-slate-200 dark:border-slate-700'}`}
          >
            From Reel title
          </button>
          <button
            onClick={() => patch({ fileNameMode: 'custom' })}
            className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${e.fileNameMode === 'custom' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30' : 'border-slate-200 dark:border-slate-700'}`}
          >
            Custom name
          </button>
        </div>
        {e.fileNameMode === 'custom' && (
          <input
            value={e.customFileName}
            onChange={(ev) => patch({ customFileName: ev.target.value })}
            placeholder="my-reel"
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800"
          />
        )}
        <div className="grid grid-cols-2 gap-2">
          <TextField label="Prefix" value={e.fileNamePrefix} onChange={(v) => patch({ fileNamePrefix: v })} />
          <TextField label="Suffix" value={e.fileNameSuffix} onChange={(v) => patch({ fileNameSuffix: v })} />
        </div>
        <Toggle label="Sequential numbering (bulk exports)" checked={e.useSequentialNumbering} onChange={(v) => patch({ useSequentialNumbering: v })} />
        <p className="truncate text-xs text-slate-400">Preview: {previewFileName}</p>
      </SectionCard>

      <SectionCard title="Export">
        {progress && (
          <div className="space-y-1">
            <ProgressBar value={progress.progress} label={stageLabel(progress.stage)} />
            <Button size="sm" variant="danger" onClick={() => handle?.cancel()}>
              Cancel
            </Button>
          </div>
        )}
        {!progress && (
          <Button variant="primary" onClick={handleExport} disabled={project.images.length === 0}>
            Export MP4
          </Button>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
        {resultUrl && (
          <a
            href={resultUrl}
            download={resultFileName ?? 'reel.mp4'}
            className="block rounded-lg bg-green-600 px-4 py-2 text-center text-sm font-medium text-white hover:bg-green-700"
          >
            Download {resultFileName}
          </a>
        )}
      </SectionCard>
    </div>
  );
}

function stageLabel(stage: ExportProgress['stage']): string {
  switch (stage) {
    case 'preparing':
      return 'Preparing…';
    case 'mixing-audio':
      return 'Mixing audio…';
    case 'rendering-frames':
      return 'Rendering frames…';
    case 'encoding':
      return 'Encoding MP4…';
    case 'done':
      return 'Done';
  }
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800"
      />
    </label>
  );
}
